﻿import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Evaluator, SUBTRACTION, Brush } from 'three-bvh-csg';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

export type ExtrusionKind = 'solid' | 'cut';
export type LineStyle = 'solid' | 'dashed';

type SweepRing = {
    start: number;
    count: number;
    isHole: boolean;
};

type SweepSection = {
    outer: THREE.Vector2[];
    holes: THREE.Vector2[][];
    flatPoints: THREE.Vector2[];
    rings: SweepRing[];
    capTriangles: number[][];
};

type SweepSample = {
    origin: THREE.Vector3;
    tangent: THREE.Vector3;
    xAxis: THREE.Vector3;
    yAxis: THREE.Vector3;
};

export interface Matrix4TransformDto {
    /**
     * 16 values in row-major order, suitable for THREE.Matrix4.set(...)
     *
     * [ m11, m12, m13, m14,
     *   m21, m22, m23, m24,
     *   m31, m32, m33, m34,
     *   m41, m42, m43, m44 ]
     */
    elements: number[];
}

export interface Point3DDto {
    x: number;
    y: number;
    z: number;
}

export interface Vector3DDto {
    x: number;
    y: number;
    z: number;
}

export interface Shape3DDto {
    startPoint: Point3DDto;
    endPoint: Point3DDto;
    isArc: boolean;
    centerPoint?: Point3DDto;
    normal?: Vector3DDto;
    radius?: number;
    isCcw?: boolean;
    isLargeArc?: boolean;
}

export interface ExtrusionDefinition {
    handleId: string;
    kind: ExtrusionKind;
    path2d: Vec2[];
    holes2d?: Vec2[][];
    length: number;
    shape3D?: Shape3DDto;
    meshPositions?: number[];
    sectionAnchor2d?: Vec2;
    transform?: Matrix4TransformDto;
    color?: number;
    opacity?: number;
    visible?: boolean;
    bodyId?: string;
}

export interface Path3DDefinition {
    handleId?: string;
    style: LineStyle;
    points: Vec3[];
    color?: number;
    visible?: boolean;
}

export interface Text3DDefinition {
    handleId: string;
    text: string;
    fontKey?: string;
    size?: number;
    depth?: number;
    color?: number;
    opacity?: number;
    visible?: boolean;
    horizontalAlign?: 'left' | 'center' | 'right';
    verticalAlign?: 'baseline' | 'middle' | 'top' | 'bottom';
    transform?: Matrix4TransformDto;
}

export interface IntermediateModelDto {
    units?: string;
    extrusions: ExtrusionDefinition[];
    paths3d?: Path3DDefinition[];
    texts3d?: Text3DDefinition[];
}

export interface ViewerOptions {
    background?: number;
    solidColor?: number;
    selectedColor?: number;
    edgeColor?: number;
    edgeThresholdAngle?: number;
    showAxes?: boolean;
    enableSelection?: boolean;

    // tijdelijk debug
    showCutDebug?: boolean;
    cutDebugColor?: number;
    cutDebugOpacity?: number;
}

export interface LoadModelRequest {
    url: string;
    method?: 'GET' | 'POST';
    body?: unknown;
    headers?: Record<string, string>;
}

export interface Polygon3DDto {
    points: Point3DDto[];
}

export interface ExtrusionPolygonSetDto {
    handleId: string;
    color: number;
    polygons: Polygon3DDto[];
}

class Arc3DCurve extends THREE.Curve<THREE.Vector3> {
    private readonly center: THREE.Vector3;
    private readonly radius: number;
    private readonly axisX: THREE.Vector3;
    private readonly axisY: THREE.Vector3;
    private readonly startAngle: number;
    private readonly deltaAngle: number;

    constructor(args: {
        center: THREE.Vector3;
        normal: THREE.Vector3;
        startPoint: THREE.Vector3;
        endPoint: THREE.Vector3;
        radius: number;
        isCcw: boolean;
        isLargeArc: boolean;
    }) {
        super();

        const {
            center,
            normal,
            startPoint,
            endPoint,
            radius,
            isCcw,
            isLargeArc,
        } = args;

        this.center = center.clone();
        this.radius = radius;

        const n = normal.clone().normalize();
        this.axisX = startPoint.clone().sub(center).normalize();
        this.axisY = new THREE.Vector3().crossVectors(n, this.axisX).normalize();

        const endVec = endPoint.clone().sub(center).normalize();

        const startAngle = 0;
        let endAngle = Math.atan2(endVec.dot(this.axisY), endVec.dot(this.axisX));
        let delta = endAngle - startAngle;

        if (isCcw) {
            while (delta < 0) delta += Math.PI * 2;
            if (isLargeArc && delta < Math.PI) delta += Math.PI * 2;
            if (!isLargeArc && delta > Math.PI) delta -= Math.PI * 2;
        } else {
            while (delta > 0) delta -= Math.PI * 2;
            if (isLargeArc && Math.abs(delta) < Math.PI) delta -= Math.PI * 2;
            if (!isLargeArc && Math.abs(delta) > Math.PI) delta += Math.PI * 2;
        }

        this.startAngle = startAngle;
        this.deltaAngle = delta;
    }

    override getPoint(t: number, target = new THREE.Vector3()): THREE.Vector3 {
        const a = this.startAngle + this.deltaAngle * t;
        return target
            .copy(this.center)
            .addScaledVector(this.axisX, Math.cos(a) * this.radius)
            .addScaledVector(this.axisY, Math.sin(a) * this.radius);
    }
}

type BlazorRef = {
    invokeMethodAsync: (methodName: string, ...args: unknown[]) => Promise<unknown>;
};

const DEFAULTS: Required<ViewerOptions> = {
    background: 0xf5f5f5,
    solidColor: 0x9aa4b2,
    selectedColor: 0xffaa00,
    edgeColor: 0x222222,
    edgeThresholdAngle: 45,
    showAxes: false,
    enableSelection: true,

    // tijdelijk debug
    showCutDebug: false,
    cutDebugColor: 0xff0000,
    cutDebugOpacity: 0.35,
};

export class ThreeCadViewer {
    private readonly host: HTMLElement;
    private readonly options: Required<ViewerOptions>;
    private readonly scene: THREE.Scene;
    private readonly perspectiveCamera: THREE.PerspectiveCamera;
    private readonly orthoCamera: THREE.OrthographicCamera;
    private activeCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    private readonly renderer: THREE.WebGLRenderer;
    private controls: OrbitControls;
    private readonly evaluator: Evaluator;
    private readonly raycaster = new THREE.Raycaster();
    private readonly pointerNdc = new THREE.Vector2();
    private readonly modelRoot = new THREE.Group();
    private readonly lineRoot = new THREE.Group();
    private readonly objectsByHandleId = new Map<string, THREE.Object3D>();
    private readonly meshesByHandleId = new Map<string, THREE.Mesh>();
    private readonly edgeLinesByHandleId = new Map<string, THREE.LineSegments>();
    private readonly originalMaterials = new Map<string, THREE.Material | THREE.Material[]>();
    private readonly highlightMaterial: THREE.MeshStandardMaterial;
    private readonly resizeObserver: ResizeObserver;
    private readonly fontLoader = new FontLoader();
    private readonly fontCache = new Map<string, Promise<any>>();

    private currentOrthoHeight = 1000;
    private lastModelBox = new THREE.Box3();
    private isOrtho = false;
    private modelTransparencyEnabled = false;
    private modelTransparencyOpacity = 0.2;
    private lastViewportWidth = -1;
    private lastViewportHeight = -1;
    private lastPixelRatio = -1;
    private readonly materialVisualState = new WeakMap<
        THREE.Material,
        {
            transparent: boolean;
            opacity: number;
            depthWrite: boolean;
        }
        >();
    private readonly visibilityObjectsByHandleId = new Map<string, THREE.Object3D>();

    //Gizmo cube
    private readonly gizmoScene = new THREE.Scene();
    private readonly gizmoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    private readonly gizmoRaycaster = new THREE.Raycaster();
    private readonly gizmoPointer = new THREE.Vector2();
    private readonly gizmoRoot = new THREE.Group();

    //
    private animationHandle = 0;
    private disposed = false;
    private blazorRef?: BlazorRef;
    private selectedHandleId?: string;
    private hasInitialZoom = false;

    private readonly pointerDownClient = new THREE.Vector2();
    private readonly lastRightClickClient = new THREE.Vector2(Number.NaN, Number.NaN);

    private rightPointerDown = false;
    private lastRightClickTime = 0;

    private readonly rightDoubleClickMs = 320;
    private readonly rightClickMovePx = 5;
    private readonly rightDoubleClickMaxGapPx = 8;


    //Hovering and measuring
    private readonly hoverPoint = new THREE.Vector3();
    private hoverHitValid = false;
    private lastHoverNotifyAt = 0;

    private readonly hoverMarker = new THREE.Mesh(
        new THREE.SphereGeometry(2.5, 12, 12),
        new THREE.MeshBasicMaterial({
            color: 0xff3366,
            depthTest: false,
            depthWrite: false
        })
    );

    private readonly measureRoot = new THREE.Group();
    private readonly measurePointA = new THREE.Mesh(
        new THREE.SphereGeometry(2.5, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0x00aa00, depthTest: false, depthWrite: false })
    );
    private readonly measurePointB = new THREE.Mesh(
        new THREE.SphereGeometry(2.5, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0x0066ff, depthTest: false, depthWrite: false })
    );
    private readonly measureLine = new THREE.Line(
        new THREE.BufferGeometry(),
        new THREE.LineBasicMaterial({ color: 0x222222, depthTest: false, depthWrite: false })
    );

    private previousMeasurePoint?: THREE.Vector3;
    private currentMeasurePoint?: THREE.Vector3;
    private lastMeasuredDistance?: number;


    private readonly debugRoot = new THREE.Group();

    constructor(host: HTMLElement, options?: ViewerOptions) {
        this.host = host;
        this.options = { ...DEFAULTS, ...(options ?? {}) };

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(this.options.background);

        this.highlightMaterial = new THREE.MeshStandardMaterial({
            color: this.options.selectedColor,
            metalness: 0.0,
            roughness: 0.6,
            side: THREE.DoubleSide,
        });

        this.perspectiveCamera = new THREE.PerspectiveCamera(45, 1, 0.1, 100000);
        this.perspectiveCamera.position.set(600, 600, 600);

        this.orthoCamera = new THREE.OrthographicCamera(-500, 500, 500, -500, 0.1, 100000);
        this.orthoCamera.position.set(600, 600, 600);
        this.orthoCamera.up.set(0, 1, 0);

        this.activeCamera = this.perspectiveCamera;

        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.renderer.setSize(1, 1, false);
        this.renderer.domElement.style.width = '100%';
        this.renderer.domElement.style.height = '100%';
        this.renderer.domElement.style.display = 'block';
        this.host.appendChild(this.renderer.domElement);

        this.controls = this.createControls(this.activeCamera);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.screenSpacePanning = true;
        this.controls.target.set(0, 0, 0);
        this.controls.minDistance = 0.1;
        this.controls.maxDistance = 1_000_000;

        this.evaluator = new Evaluator();
        this.evaluator.useGroups = false;
        (this.evaluator as any).useCDTClipping = true;

        this.hoverMarker.visible = false;
        this.scene.add(this.hoverMarker);

        this.measurePointA.visible = false;
        this.measurePointB.visible = false;
        this.measureLine.visible = false;

        this.measureRoot.add(this.measurePointA);
        this.measureRoot.add(this.measurePointB);
        this.measureRoot.add(this.measureLine);

        this.scene.add(this.measureRoot);
        this.scene.add(this.modelRoot);
        this.scene.add(this.lineRoot);
        this.scene.add(this.debugRoot);

        this.addLights();

        if (this.options.showAxes) {
            this.scene.add(new THREE.AxesHelper(200));
        }

        this.renderer.domElement.addEventListener('click', this.onCanvasClick);
        this.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        this.renderer.domElement.addEventListener('pointerup', this.onPointerUp);
        this.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);
        this.renderer.domElement.addEventListener('pointermove', this.onPointerMove);

        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.host);
        window.addEventListener('resize', this.onWindowResize);
        window.visualViewport?.addEventListener('resize', this.onVisualViewportResize);

        this.gizmoCamera.position.set(0, 0, 5);
        this.gizmoScene.add(this.gizmoRoot);
        this.gizmoRoot.add(this.createViewCube());

        this.resize();
        this.animate();
    }

    private readonly onWindowResize = (): void => {
        this.resize();
    };

    private readonly onVisualViewportResize = (): void => {
        this.resize();
    };

    private createControls(camera: THREE.Camera): OrbitControls {
        const controls = new OrbitControls(camera, this.renderer.domElement);

        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.screenSpacePanning = true;
        controls.target.set(0, 0, 0);
        controls.minDistance = 0.1;
        controls.maxDistance = 1_000_000;

        controls.mouseButtons = {
            LEFT: -1 as any,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.ROTATE,
        };

        return controls;
    }

    private registerMeasurementPoint(point: THREE.Vector3): void {
        const next = point.clone();

        if (!this.previousMeasurePoint) {
            this.previousMeasurePoint = next;
            this.currentMeasurePoint = undefined;
            this.lastMeasuredDistance = undefined;

            this.measurePointA.position.copy(next);
            this.measurePointA.visible = true;
            this.measurePointB.visible = false;
            this.measureLine.visible = false;
            return;
        }

        this.currentMeasurePoint = next;
        this.lastMeasuredDistance = this.previousMeasurePoint.distanceTo(this.currentMeasurePoint);

        this.measurePointA.position.copy(this.previousMeasurePoint);
        this.measurePointB.position.copy(this.currentMeasurePoint);
        this.measurePointA.visible = true;
        this.measurePointB.visible = true;

        const positions = new Float32Array([
            this.previousMeasurePoint.x, this.previousMeasurePoint.y, this.previousMeasurePoint.z,
            this.currentMeasurePoint.x, this.currentMeasurePoint.y, this.currentMeasurePoint.z,
        ]);

        this.measureLine.geometry.dispose();
        this.measureLine.geometry = new THREE.BufferGeometry();
        this.measureLine.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.measureLine.visible = true;

        console.log('Measured distance:', this.lastMeasuredDistance);

        void this.notifyMeasurement(this.lastMeasuredDistance, this.previousMeasurePoint, this.currentMeasurePoint);

        // schuif venster door: laatste punt wordt het nieuwe "vorige"
        this.previousMeasurePoint = next.clone();
    }

    private createViewCube(): THREE.Object3D {
        const group = new THREE.Group();

        const wire = new THREE.LineSegments(
            new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
            new THREE.LineBasicMaterial({ color: 0x333333 })
        );
        group.add(wire);

        const faceMat = (color: number) =>
            new THREE.MeshBasicMaterial({
                color,
                transparent: true,
                opacity: 0.9,
                side: THREE.DoubleSide
            });

        const makeFace = (
            name: string,
            position: THREE.Vector3,
            rotation: THREE.Euler,
            color: number
        ) => {
            const face = new THREE.Mesh(
                new THREE.PlaneGeometry(0.9, 0.9),
                faceMat(color)
            );
            face.name = name;
            face.position.copy(position);
            face.rotation.copy(rotation);
            group.add(face);
        };

        makeFace('right', new THREE.Vector3(0.5, 0, 0), new THREE.Euler(0, Math.PI / 2, 0), 0xffcccc);
        makeFace('left', new THREE.Vector3(-0.5, 0, 0), new THREE.Euler(0, -Math.PI / 2, 0), 0xffdddd);
        makeFace('top', new THREE.Vector3(0, 0.5, 0), new THREE.Euler(-Math.PI / 2, 0, 0), 0xccffcc);
        makeFace('bottom', new THREE.Vector3(0, -0.5, 0), new THREE.Euler(Math.PI / 2, 0, 0), 0xddffdd);
        makeFace('front', new THREE.Vector3(0, 0, 0.5), new THREE.Euler(0, 0, 0), 0xccccff);
        makeFace('back', new THREE.Vector3(0, 0, -0.5), new THREE.Euler(0, Math.PI, 0), 0xddddff);

        return group;
    }

    public setProjectionMode(mode: 'perspective' | 'orthographic'): void {
        if (mode === 'orthographic') {
            this.switchToOrtho();
        } else {
            this.switchToPerspective();
        }

        this.activeCamera.lookAt(this.controls.target);
        this.controls.update();
    }

    public toggleProjectionMode(): void {
        this.setProjectionMode(this.activeCamera === this.perspectiveCamera ? 'orthographic' : 'perspective');
    }

    private switchToPerspective(): void {
        if (this.activeCamera === this.perspectiveCamera) return;

        const target = this.controls.target.clone();
        const position = this.activeCamera.position.clone();
        const up = this.activeCamera.up.clone();

        this.controls.dispose();

        this.activeCamera = this.perspectiveCamera;
        this.perspectiveCamera.position.copy(position);
        this.perspectiveCamera.up.copy(up);

        this.controls = this.createControls(this.activeCamera);
        this.controls.target.copy(target);
        this.controls.update();

        this.isOrtho = false;
        this.resize();
    }

    private switchToOrtho(): void {
        if (this.activeCamera === this.orthoCamera) return;

        const target = this.controls.target.clone();
        const position = this.activeCamera.position.clone();
        const up = this.activeCamera.up.clone();

        this.controls.dispose();

        this.activeCamera = this.orthoCamera;
        this.orthoCamera.position.copy(position);
        this.orthoCamera.up.copy(up);

        this.controls = this.createControls(this.activeCamera);
        this.controls.target.copy(target);

        // ortho fit behouden
        this.updateOrthoFromCurrentView();

        this.controls.update();
        this.isOrtho = true;
        this.resize();
    }

    private updateOrthoFromCurrentView(): void {
        const box = this.lastModelBox.isEmpty()
            ? new THREE.Box3().setFromObject(this.modelRoot)
            : this.lastModelBox.clone();

        if (box.isEmpty()) {
            this.currentOrthoHeight = 1000;
            this.updateOrthoFrustum();
            return;
        }

        const size = box.getSize(new THREE.Vector3());
        const target = this.controls.target.clone();
        const dir = this.activeCamera.position.clone().sub(target).normalize();

        const ax = Math.abs(dir.x);
        const ay = Math.abs(dir.y);
        const az = Math.abs(dir.z);

        let orthoW = size.x;
        let orthoH = size.y;

        // bepaal welke projectievlak het meest logisch is vanuit huidige kijkrichting
        if (ax >= ay && ax >= az) {
            orthoW = size.z;
            orthoH = size.y;
        } else if (ay >= ax && ay >= az) {
            orthoW = size.x;
            orthoH = size.z;
        } else {
            orthoW = size.x;
            orthoH = size.y;
        }

        const aspect = Math.max(this.host.clientWidth, 1) / Math.max(this.host.clientHeight, 1);
        const paddedH = orthoH * 1.2;
        const paddedW = orthoW * 1.2;

        this.currentOrthoHeight = Math.max(paddedH, paddedW / aspect, 1);
        this.updateOrthoFrustum();
    }

    private updateOrthoFrustum(): void {
        const width = Math.max(this.host.clientWidth, 1);
        const height = Math.max(this.host.clientHeight, 1);
        const aspect = width / height;

        const halfH = this.currentOrthoHeight / 2;
        const halfW = halfH * aspect;

        this.orthoCamera.left = -halfW;
        this.orthoCamera.right = halfW;
        this.orthoCamera.top = halfH;
        this.orthoCamera.bottom = -halfH;
        this.orthoCamera.near = 0.1;
        this.orthoCamera.far = 1_000_000;
        this.orthoCamera.updateProjectionMatrix();
    }

    public setBlazorRef(ref: BlazorRef | undefined): void {
        this.blazorRef = ref;
    }

    private getFontUrl(fontKey?: string): string {
        switch (fontKey) {
            case 'helvetiker-bold':
                return '/fonts/helvetiker_bold.typeface.json';
            case 'helvetiker-regular':
            default:
                return '/fonts/helvetiker_regular.typeface.json';
        }
    }

    private loadFont(fontKey?: string): Promise<any> {
        const key = fontKey ?? 'helvetiker-regular';

        const existing = this.fontCache.get(key);
        if (existing) {
            return existing;
        }

        const promise = this.fontLoader
            .loadAsync(this.getFontUrl(key))
            .catch(error => {
                this.fontCache.delete(key);
                throw error;
            });

        this.fontCache.set(key, promise);
        return promise;
    }

    public async loadModel(request: LoadModelRequest): Promise<void> {
        const dto = await this.fetchModel(request);
        await this.setModel(dto);
    }

    public async loadMockModel(): Promise<void> {
        const dto = await mockFetchIntermediateModel();
        await this.setModel(dto);
    }

    private async fetchModel(request: LoadModelRequest): Promise<IntermediateModelDto> {
        const response = await fetch(request.url, {
            method: request.method ?? 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(request.headers ?? {}),
            },
            body: request.body !== undefined ? JSON.stringify(request.body) : undefined,
        });

        if (!response.ok) {
            throw new Error(`Failed to load model: ${response.status} ${response.statusText}`);
        }

        return (await response.json()) as IntermediateModelDto;
    }

    public async setModel(dto: IntermediateModelDto): Promise<void> {
        this.clearScene();

        const grouped = this.groupExtrusionsByBody(dto.extrusions ?? []);

        for (const [, bodyExtrusions] of grouped) {
            const bodyObject = this.buildBody(bodyExtrusions);
            if (bodyObject) {
                this.modelRoot.add(bodyObject);
            }
        }

        for (const path of dto.paths3d ?? []) {
            const line = this.buildPath3D(path);
            if (line) {
                this.lineRoot.add(line);
            }
        }

        for (const text of dto.texts3d ?? []) {
            const mesh = await this.buildText3D(text);
            if (mesh) {
                this.lineRoot.add(mesh);
            }
        }

        this.applyModelTransparency();
        this.updateLastModelBox();

        if (!this.hasInitialZoom && !this.lastModelBox.isEmpty()) {
            this.zoomAll();
            this.hasInitialZoom = true;
        } else {
            this.render();
        }
    }

    private updateLastModelBox(): void {
        const box = new THREE.Box3().setFromObject(this.modelRoot);
        const lineBox = new THREE.Box3().setFromObject(this.lineRoot);

        if (!lineBox.isEmpty()) {
            box.union(lineBox);
        }

        this.lastModelBox.copy(box);
    }

    private async buildText3D(def: Text3DDefinition): Promise<THREE.Mesh | null> {
        if (def.visible === false) return null;
        if (!def.text || !def.text.trim()) return null;

        const font = await this.loadFont(def.fontKey);

        const geometry = new TextGeometry(def.text, {
            font,
            size: def.size ?? 20,
            depth: def.depth ?? 2,
            curveSegments: 8,
            bevelEnabled: false,
        });

        geometry.computeBoundingBox();

        const bbox = geometry.boundingBox;
        if (bbox) {
            const size = bbox.getSize(new THREE.Vector3());
            const offset = new THREE.Vector3();

            switch (def.horizontalAlign ?? 'left') {
                case 'center':
                    offset.x = -size.x / 2;
                    break;
                case 'right':
                    offset.x = -size.x;
                    break;
            }

            switch (def.verticalAlign ?? 'baseline') {
                case 'middle':
                    offset.y = -size.y / 2;
                    break;
                case 'top':
                    offset.y = -size.y;
                    break;
                case 'bottom':
                case 'baseline':
                    break;
            }

            geometry.translate(offset.x, offset.y, offset.z);
        }

        geometry.computeVertexNormals();

        const mesh = new THREE.Mesh(
            geometry,
            this.createSolidMaterial(def.color, def.opacity)
        );

        mesh.userData.handleId = def.handleId;
        mesh.name = def.handleId;

        const matrix = this.getTransformMatrix(def.transform);
        mesh.applyMatrix4(matrix);
        mesh.updateMatrixWorld(true);

        this.objectsByHandleId.set(def.handleId, mesh);
        this.visibilityObjectsByHandleId.set(def.handleId, mesh);

        return mesh;
    }

    public zoomAll(paddingFactor = 1.2): void {
        const box = new THREE.Box3().setFromObject(this.modelRoot);
        const lineBox = new THREE.Box3().setFromObject(this.lineRoot);

        if (!lineBox.isEmpty()) {
            box.union(lineBox);
        }

        if (box.isEmpty()) {
            this.switchToPerspective();
            this.controls.target.set(0, 0, 0);
            this.perspectiveCamera.position.set(0, 0, 600);
            this.perspectiveCamera.up.set(0, 1, 0);
            this.perspectiveCamera.lookAt(this.controls.target);
            this.controls.update();
            return;
        }

        this.lastModelBox.copy(box);

        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // frontaanzicht: we kijken langs Z, dus X/Y moeten in beeld passen
        const viewWidth = Math.max(size.x, 1);
        const viewHeight = Math.max(size.y, 1);

        const fov = THREE.MathUtils.degToRad(this.perspectiveCamera.fov);
        const fitHeightDistance = (viewHeight * paddingFactor) / (2 * Math.tan(fov / 2));
        const fitWidthDistance = (viewWidth * paddingFactor) / (2 * Math.tan(fov / 2) * this.perspectiveCamera.aspect);

        // extra marge voor diepte van het model
        const distance = Math.max(fitHeightDistance, fitWidthDistance) + size.z * 0.5 + 1;

        // kijk van Z+ naar Z-
        const newPosition = new THREE.Vector3(center.x, center.y, center.z + distance);

        this.controls.target.copy(center);

        this.perspectiveCamera.position.copy(newPosition);
        this.perspectiveCamera.up.set(0, 1, 0);
        this.perspectiveCamera.near = Math.max(distance / 1000, 0.1);
        this.perspectiveCamera.far = Math.max(distance * 20, 1000);
        this.perspectiveCamera.updateProjectionMatrix();

        // orthografische camera ook netjes op dezelfde kijkrichting zetten
        this.currentOrthoHeight = Math.max(viewHeight * paddingFactor, (viewWidth * paddingFactor) / this.perspectiveCamera.aspect, 1);
        this.updateOrthoFrustum();

        this.orthoCamera.position.copy(newPosition);
        this.orthoCamera.up.set(0, 1, 0);
        this.orthoCamera.lookAt(center);

        this.activeCamera.lookAt(center);
        this.controls.update();
    }

    public clearSelection(): void {
        if (!this.selectedHandleId) return;

        const handleId = this.selectedHandleId;
        const mesh = this.meshesByHandleId.get(handleId);
        const original = this.originalMaterials.get(handleId);

        if (mesh && original) {
            mesh.material = original;
            this.applyTransparencyToMaterialValue(original);
        }

        const edgeLines = this.edgeLinesByHandleId.get(handleId);
        if (edgeLines) {
            const material = edgeLines.material as THREE.LineBasicMaterial;
            material.color.setHex(this.options.edgeColor);
        }

        this.originalMaterials.delete(handleId);
        this.selectedHandleId = undefined;
    }

    public selectByHandleId(handleId: string | undefined, notifyBlazor = false): void {
        this.clearSelection();

        if (!handleId) {
            return;
        }

        const mesh = this.meshesByHandleId.get(handleId);
        if (!mesh) {
            return;
        }

        this.originalMaterials.set(handleId, mesh.material);
        mesh.material = this.highlightMaterial;
        this.applyTransparencyToMaterialValue(mesh.material);

        const edgeLines = this.edgeLinesByHandleId.get(handleId);
        if (edgeLines) {
            const material = edgeLines.material as THREE.LineBasicMaterial;
            material.color.setHex(this.options.selectedColor);
        }

        this.selectedHandleId = handleId;

        if (notifyBlazor) {
            void this.notifySelection(handleId);
        }
    }

    public setHandlesVisibility(handleIds: string[], visible: boolean): void {
        if (!handleIds || handleIds.length === 0) {
            return;
        }

        if (!visible && this.selectedHandleId && handleIds.includes(this.selectedHandleId)) {
            this.clearSelection();
        }

        for (const handleId of handleIds) {
            const obj = this.visibilityObjectsByHandleId.get(handleId);
            if (!obj) {
                continue;
            }

            obj.visible = visible;
        }

        this.render();
    }

    private onContextMenu = (event: MouseEvent): void => {
        event.preventDefault();
    };

    private onPointerDown = (event: PointerEvent): void => {
        if (event.button !== 2) return;

        this.rightPointerDown = true;
        this.pointerDownClient.set(event.clientX, event.clientY);
    };

    private onPointerUp = (event: PointerEvent): void => {
        if (event.button !== 2 || !this.rightPointerDown) {
            return;
        }

        this.rightPointerDown = false;

        const up = new THREE.Vector2(event.clientX, event.clientY);
        const movedPx = up.distanceTo(this.pointerDownClient);

        // was het eigenlijk een drag / pan?
        if (movedPx > this.rightClickMovePx) {
            return;
        }

        const now = performance.now();
        const withinTime = now - this.lastRightClickTime <= this.rightDoubleClickMs;
        const withinGap =
            Number.isFinite(this.lastRightClickClient.x) &&
            up.distanceTo(this.lastRightClickClient) <= this.rightDoubleClickMaxGapPx;

        this.lastRightClickTime = now;
        this.lastRightClickClient.copy(up);

        if (!withinTime || !withinGap) {
            return;
        }

        event.preventDefault();
        this.setOrbitTargetFromPointerEvent(event);
    };

    private onPointerMove = (event: PointerEvent): void => {
        this.updateHoverPick(event.clientX, event.clientY);
    };

    public dispose(): void {
        if (this.disposed) return;
        this.disposed = true;

        cancelAnimationFrame(this.animationHandle);
        this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.onWindowResize);
        window.visualViewport?.removeEventListener('resize', this.onVisualViewportResize);
        this.renderer.domElement.removeEventListener('click', this.onCanvasClick);
        this.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.renderer.domElement.removeEventListener('pointerup', this.onPointerUp);
        this.renderer.domElement.removeEventListener('contextmenu', this.onContextMenu);
        this.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.controls.dispose();
        this.clearScene();
        this.disposeObject(this.hoverMarker, false);
        this.disposeObject(this.measureRoot, false);
        this.disposeObject(this.gizmoRoot, false);
        this.scene.clear();
        this.renderer.renderLists.dispose();
        this.renderer.dispose();
        this.renderer.forceContextLoss();
        this.highlightMaterial.dispose();

        if (this.renderer.domElement.parentElement === this.host) {
            this.host.removeChild(this.renderer.domElement);
        }

        this.blazorRef = undefined;
    }

    private addLights(): void {
        const ambient = new THREE.AmbientLight(0xffffff, 0.75);
        this.scene.add(ambient);

        const directional1 = new THREE.DirectionalLight(0xffffff, 1.0);
        directional1.position.set(300, 500, 400);
        this.scene.add(directional1);

        const directional2 = new THREE.DirectionalLight(0xffffff, 0.45);
        directional2.position.set(-400, 250, -300);
        this.scene.add(directional2);
    }

    private resize(): void {
        const width = Math.max(Math.round(this.host.clientWidth), 1);
        const height = Math.max(Math.round(this.host.clientHeight), 1);
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

        this.lastViewportWidth = width;
        this.lastViewportHeight = height;
        this.lastPixelRatio = pixelRatio;

        this.renderer.setPixelRatio(pixelRatio);
        this.renderer.setSize(width, height, false);

        this.perspectiveCamera.aspect = width / height;
        this.perspectiveCamera.updateProjectionMatrix();

        this.updateOrthoFrustum();

        this.render();
    }

    private animate = (): void => {
        if (this.disposed) return;
        this.animationHandle = requestAnimationFrame(this.animate);

        if (this.cameraAnim) {
            const tRaw = (performance.now() - this.cameraAnim.startTime) / this.cameraAnim.durationMs;
            const t = Math.min(Math.max(tRaw, 0), 1);
            const s = t * t * (3 - 2 * t);

            this.activeCamera.position.lerpVectors(this.cameraAnim.fromPos, this.cameraAnim.toPos, s);
            this.activeCamera.up.lerpVectors(this.cameraAnim.fromUp, this.cameraAnim.toUp, s);
            this.activeCamera.lookAt(this.cameraAnim.target);

            this.controls.target.copy(this.cameraAnim.target);
            this.controls.update();

            if (t >= 1) {
                this.cameraAnim = undefined;
            }
        } else {
            this.controls.update();
        }

        this.gizmoRoot.quaternion.copy(this.activeCamera.quaternion).invert();
        this.render();
    };

    private render(): void {
        const w = this.renderer.domElement.clientWidth;
        const h = this.renderer.domElement.clientHeight;

        this.renderer.setViewport(0, 0, w, h);
        this.renderer.setScissorTest(false);
        this.renderer.render(this.scene, this.activeCamera);

        //Gizmo cube
        const size = 100;
        const margin = 12;
        const x = w - size - margin;
        const y = margin;
        this.renderer.clearDepth();
        this.renderer.setViewport(x, y, size, size);
        this.renderer.setScissor(x, y, size, size);
        this.renderer.setScissorTest(true);
        this.renderer.render(this.gizmoScene, this.gizmoCamera);
        this.renderer.setScissorTest(false);
    }

    private clearScene(): void {
        this.clearSelection();
        this.disposeObject(this.modelRoot, false);
        this.disposeObject(this.lineRoot, false);
        this.disposeObject(this.debugRoot, false);
        this.modelRoot.clear();
        this.lineRoot.clear();
        this.debugRoot.clear();
        this.objectsByHandleId.clear();
        this.meshesByHandleId.clear();
        this.edgeLinesByHandleId.clear();
        this.originalMaterials.clear();
        this.visibilityObjectsByHandleId.clear();
    }


    private groupExtrusionsByBody(extrusions: ExtrusionDefinition[]): Map<string, ExtrusionDefinition[]> {
        const groups = new Map<string, ExtrusionDefinition[]>();

        for (const ext of extrusions) {
            const key = ext.bodyId ?? ext.handleId;
            const current = groups.get(key);
            if (current) {
                current.push(ext);
            } else {
                groups.set(key, [ext]);
            }
        }

        return groups;
    }

    private buildBody(extrusions: ExtrusionDefinition[]): THREE.Object3D | null {
        const visible = extrusions.filter(x => x.visible !== false);
        if (visible.length === 0) return null;

        const solids = visible.filter(x => x.kind === 'solid');
        const cuts = visible.filter(x => x.kind === 'cut');

        if (solids.length === 0) return null;
        if (solids.length > 1) {
            throw new Error('Each body can only contain one solid source extrusion.');
        }

        const source = solids[0];
        const sourceWorldMatrix = this.getTransformMatrix(source.transform);
        const sourceWorldInverse = sourceWorldMatrix.clone().invert();

        let currentBrush = this.createBrushFromExtrusion(source);

        for (const cut of cuts) {
            const cutBrush = this.createBrushFromExtrusion(cut);

            const cutWorldMatrix = this.getTransformMatrix(cut.transform);
            const cutLocalToSource = sourceWorldInverse.clone().multiply(cutWorldMatrix);

            // debug: toon cut in wereldcoördinaten
            if (this.options.showCutDebug && cut.shape3D?.isArc === true) {
                const cutDebugGeometry = this.transformGeometry(cutBrush.geometry, cutWorldMatrix);

                const cutDebugMesh = new THREE.Mesh(
                    cutDebugGeometry,
                    this.createCutDebugMaterial()
                );

                cutDebugMesh.name = `debug-cut-${cut.handleId}`;
                cutDebugMesh.userData.handleId = cut.handleId;
                this.debugRoot.add(cutDebugMesh);
            }

            cutBrush.geometry = this.transformGeometry(cutBrush.geometry, cutLocalToSource);
            cutBrush.updateMatrixWorld(true);

            currentBrush = this.evaluator.evaluate(currentBrush, cutBrush, SUBTRACTION) as Brush;
        }

        const finalGeometry = this.transformGeometry(currentBrush.geometry, sourceWorldMatrix);

        const resultMesh = new THREE.Mesh(
            finalGeometry,
            this.createSolidMaterial(source.color, source.opacity)
        );

        resultMesh.name = source.bodyId ?? source.handleId;
        resultMesh.userData.handleId = source.handleId;
        resultMesh.userData.sourceHandleIds = [source.handleId];
        resultMesh.userData.exportColorByHandleId = {
            [source.handleId]: this.toArgbColor(
                source.color ?? this.options.solidColor,
                source.opacity ?? 1
            )
        };
        resultMesh.castShadow = false;
        resultMesh.receiveShadow = false;

        const edges = this.createEdges(finalGeometry);
        edges.userData.handleId = source.handleId;

        const group = new THREE.Group();
        group.add(resultMesh);
        group.add(edges);

        for (const extrusion of extrusions) {
            this.visibilityObjectsByHandleId.set(extrusion.handleId, group);
        }

        this.objectsByHandleId.set(source.handleId, resultMesh);
        this.meshesByHandleId.set(source.handleId, resultMesh);
        this.edgeLinesByHandleId.set(source.handleId, edges);

        return group;
    }

    private getTransformMatrix(transform?: Matrix4TransformDto): THREE.Matrix4 {
        const matrix = new THREE.Matrix4();

        if (!transform?.elements || transform.elements.length !== 16) {
            return matrix.identity();
        }

        matrix.set(
            transform.elements[0], transform.elements[1], transform.elements[2], transform.elements[3],
            transform.elements[4], transform.elements[5], transform.elements[6], transform.elements[7],
            transform.elements[8], transform.elements[9], transform.elements[10], transform.elements[11],
            transform.elements[12], transform.elements[13], transform.elements[14], transform.elements[15]
        );

        return matrix;
    }

    private transformGeometry(
        geometry: THREE.BufferGeometry,
        matrix: THREE.Matrix4
    ): THREE.BufferGeometry {
        const result = geometry.clone();
        result.applyMatrix4(matrix);
        result.computeVertexNormals();
        result.computeBoundingBox();
        result.computeBoundingSphere();
        return result;
    }

    private createBrushFromExtrusion(def: ExtrusionDefinition): Brush {
        let geometry: THREE.BufferGeometry;
        if (def.meshPositions) {
            if (def.meshPositions.length < 9 || def.meshPositions.length % 9 !== 0 || !def.meshPositions.every(Number.isFinite)) {
                throw new Error('Mesh positions must contain finite triangle vertices.');
            }
            geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(def.meshPositions, 3));
        } else {
            const shape = this.createShape(def.path2d, def.holes2d ?? []);
            geometry = def.shape3D
                ? this.createSweepGeometry(shape, def)
                : new THREE.ExtrudeGeometry(shape, {
                    depth: def.length,
                    bevelEnabled: false,
                    steps: 1,
                    curveSegments: 24,
                });
        }

        geometry.clearGroups();
        geometry.computeVertexNormals();
        geometry = this.ensureGeometryHasCsgAttributes(geometry);

        const brush = new Brush(
            geometry,
            this.createSolidMaterial(def.color, def.opacity)
        );

        brush.updateMatrixWorld(true);
        return brush;
    }

    private ensureGeometryHasCsgAttributes(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
        const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        if (!position) {
            throw new Error('Geometry must have a position attribute.');
        }

        if (!geometry.getAttribute('normal')) {
            geometry.computeVertexNormals();
        }

        if (!geometry.getAttribute('uv')) {
            const uv = new Float32Array(position.count * 2);
            geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        }

        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        return geometry;
    }

    private createShape(outer: Vec2[], holes: Vec2[][]): THREE.Shape {
        if (!outer || outer.length < 3) {
            throw new Error('Extrusion path2d must contain at least 3 points.');
        }

        const shape = new THREE.Shape();
        shape.moveTo(outer[0][0], outer[0][1]);
        for (let i = 1; i < outer.length; i++) {
            shape.lineTo(outer[i][0], outer[i][1]);
        }
        shape.lineTo(outer[0][0], outer[0][1]);

        for (const holePoints of holes) {
            if (!holePoints || holePoints.length < 3) continue;

            const hole = new THREE.Path();
            hole.moveTo(holePoints[0][0], holePoints[0][1]);
            for (let i = 1; i < holePoints.length; i++) {
                hole.lineTo(holePoints[i][0], holePoints[i][1]);
            }
            hole.lineTo(holePoints[0][0], holePoints[0][1]);
            shape.holes.push(hole);
        }

        return shape;
    }

    private toVector3(p: Point3DDto | Vector3DDto): THREE.Vector3 {
        return new THREE.Vector3(p.x, p.y, p.z);
    }

    private stripClosingPoint(points: THREE.Vector2[]): THREE.Vector2[] {
        if (points.length <= 1) return points.slice();

        const first = points[0];
        const last = points[points.length - 1];

        if (first.distanceToSquared(last) < 1e-12) {
            return points.slice(0, points.length - 1);
        }

        return points.slice();
    }

    private normalizeRing(points: THREE.Vector2[], wantClockwise: boolean): THREE.Vector2[] {
        let result = this.stripClosingPoint(points);

        if (result.length < 3) {
            return [];
        }

        const isClockwise = THREE.ShapeUtils.isClockWise(result);
        if (isClockwise !== wantClockwise) {
            result = result.slice().reverse();
        }

        return result;
    }

    private extractSweepSection(shape: THREE.Shape, curveSegments = 24): SweepSection {
        const extracted = shape.extractPoints(curveSegments);

        const outer = this.normalizeRing(extracted.shape, false); // outer CCW
        const holes = (extracted.holes ?? [])
            .map(h => this.normalizeRing(h, true)) // holes CW
            .filter(h => h.length >= 3);

        const flatPoints: THREE.Vector2[] = [];
        const rings: SweepRing[] = [];

        flatPoints.push(...outer);
        rings.push({
            start: 0,
            count: outer.length,
            isHole: false
        });

        for (const hole of holes) {
            const start = flatPoints.length;
            flatPoints.push(...hole);
            rings.push({
                start,
                count: hole.length,
                isHole: true
            });
        }

        const capTriangles = THREE.ShapeUtils.triangulateShape(outer, holes);

        return {
            outer,
            holes,
            flatPoints,
            rings,
            capTriangles
        };
    }

    private createSweepGeometry(shape: THREE.Shape, def: ExtrusionDefinition): THREE.BufferGeometry {
        const pathDef = def.shape3D;
        if (!pathDef) {
            throw new Error('createSweepGeometry requires def.shape3D.');
        }

        const section = this.extractSweepSection(shape, 24);
        const samples = this.buildSweepSamples(pathDef);

        if (section.flatPoints.length < 3) {
            throw new Error('Sweep section must contain at least 3 points.');
        }

        if (samples.length < 2) {
            throw new Error('Sweep path must contain at least 2 samples.');
        }

        const anchorX = def.sectionAnchor2d?.[0] ?? 0;
        const anchorY = def.sectionAnchor2d?.[1] ?? 0;

        const sectionPointCount = section.flatPoints.length;
        const positions: number[] = [];
        const indices: number[] = [];

        const temp = new THREE.Vector3();

        // Side vertices: one full ring per sample
        for (const sample of samples) {
            for (const p of section.flatPoints) {
                const localX = p.x - anchorX;
                const localY = p.y - anchorY;

                temp.copy(sample.origin)
                    .addScaledVector(sample.xAxis, localX)
                    .addScaledVector(sample.yAxis, localY);

                positions.push(temp.x, temp.y, temp.z);
            }
        }

        const sideIndex = (sampleIndex: number, pointIndex: number) =>
            sampleIndex * sectionPointCount + pointIndex;

        // Side quads
        for (let s = 0; s < samples.length - 1; s++) {
            for (const ring of section.rings) {
                for (let i = 0; i < ring.count; i++) {
                    const iNext = (i + 1) % ring.count;

                    const a0 = sideIndex(s, ring.start + i);
                    const a1 = sideIndex(s, ring.start + iNext);
                    const b0 = sideIndex(s + 1, ring.start + i);
                    const b1 = sideIndex(s + 1, ring.start + iNext);

                    indices.push(a0, a1, b1);
                    indices.push(a0, b1, b0);
                }
            }
        }

        // Start cap
        const startCapOffset = positions.length / 3;
        {
            const sample = samples[0];

            for (const p of section.flatPoints) {
                const localX = p.x - anchorX;
                const localY = p.y - anchorY;

                temp.copy(sample.origin)
                    .addScaledVector(sample.xAxis, localX)
                    .addScaledVector(sample.yAxis, localY);

                positions.push(temp.x, temp.y, temp.z);
            }

            for (const tri of section.capTriangles) {
                indices.push(
                    startCapOffset + tri[2],
                    startCapOffset + tri[1],
                    startCapOffset + tri[0]
                );
            }
        }

        // End cap
        const endCapOffset = positions.length / 3;
        {
            const sample = samples[samples.length - 1];

            for (const p of section.flatPoints) {
                const localX = p.x - anchorX;
                const localY = p.y - anchorY;

                temp.copy(sample.origin)
                    .addScaledVector(sample.xAxis, localX)
                    .addScaledVector(sample.yAxis, localY);

                positions.push(temp.x, temp.y, temp.z);
            }

            for (const tri of section.capTriangles) {
                indices.push(
                    endCapOffset + tri[0],
                    endCapOffset + tri[1],
                    endCapOffset + tri[2]
                );
            }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        );
        geometry.setIndex(indices);

        this.flipIndexedGeometryWinding(geometry);

        geometry.computeVertexNormals();

        const position = geometry.getAttribute('position') as THREE.BufferAttribute;
        if (!geometry.getAttribute('uv')) {
            const uv = new Float32Array(position.count * 2);
            geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        }

        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        return geometry;
    }

    private buildSweepSamples(pathDef: Shape3DDto): SweepSample[] {
        return pathDef.isArc
            ? this.buildArcSweepSamples(pathDef)
            : this.buildLineSweepSamples(pathDef);
    }

    private buildLineSweepSamples(pathDef: Shape3DDto): SweepSample[] {
        const start = this.toVector3(pathDef.startPoint);
        const end = this.toVector3(pathDef.endPoint);

        const tangent = end.clone().sub(start).normalize();
        if (tangent.lengthSq() < 1e-12) {
            throw new Error('Line sweep path has zero length.');
        }

        const yAxis = this.resolveSectionYAxis(tangent, pathDef.normal);
        const xAxis = new THREE.Vector3().crossVectors(tangent, yAxis).normalize();

        return [
            {
                origin: start,
                tangent: tangent.clone(),
                xAxis: xAxis.clone(),
                yAxis: yAxis.clone()
            },
            {
                origin: end,
                tangent: tangent.clone(),
                xAxis: xAxis.clone(),
                yAxis: yAxis.clone()
            }
        ];
    }

    private buildArcSweepSamples(pathDef: Shape3DDto): SweepSample[] {
        if (!pathDef.centerPoint || !pathDef.normal) {
            throw new Error('Arc sweep requires centerPoint and normal.');
        }

        const start = this.toVector3(pathDef.startPoint);
        const end = this.toVector3(pathDef.endPoint);
        const center = this.toVector3(pathDef.centerPoint);
        const normal = this.toVector3(pathDef.normal).normalize();

        const planeX = start.clone().sub(center);
        const radius = pathDef.radius ?? planeX.length();

        if (radius <= 0) {
            throw new Error('Arc sweep radius must be > 0.');
        }

        if (planeX.lengthSq() < 1e-12) {
            throw new Error('Arc sweep startPoint cannot equal centerPoint.');
        }

        planeX.normalize();

        const planeY = new THREE.Vector3().crossVectors(normal, planeX).normalize();
        const endVec = end.clone().sub(center).normalize();

        let delta = Math.atan2(
            endVec.dot(planeY),
            endVec.dot(planeX)
        );

        const isCcw = pathDef.isCcw === true;
        const isLargeArc = pathDef.isLargeArc === true;

        if (isCcw) {
            while (delta < 0) delta += Math.PI * 2;
            if (isLargeArc && delta < Math.PI) delta += Math.PI * 2;
            if (!isLargeArc && delta > Math.PI) delta -= Math.PI * 2;
        } else {
            while (delta > 0) delta -= Math.PI * 2;
            if (isLargeArc && Math.abs(delta) < Math.PI) delta -= Math.PI * 2;
            if (!isLargeArc && Math.abs(delta) > Math.PI) delta += Math.PI * 2;
        }

        const arcLength = Math.abs(delta) * radius;
        const segments = Math.max(
            8,
            Math.ceil(Math.abs(delta) / THREE.MathUtils.degToRad(5)),
            Math.ceil(arcLength / 20)
        );

        const samples: SweepSample[] = [];

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = delta * t;

            const cos = Math.cos(angle);
            const sin = Math.sin(angle);

            const radial = planeX.clone().multiplyScalar(cos)
                .addScaledVector(planeY, sin);

            const origin = center.clone().addScaledVector(radial, radius);

            // derivative wrt angle, then corrected for direction sign via delta
            const tangentBase = planeX.clone().multiplyScalar(-sin)
                .addScaledVector(planeY, cos);

            const tangent = tangentBase.multiplyScalar(delta >= 0 ? 1 : -1).normalize();

            const yAxis = normal.clone();
            const xAxis = new THREE.Vector3().crossVectors(tangent, yAxis).normalize();

            samples.push({
                origin,
                tangent,
                xAxis,
                yAxis
            });
        }

        return samples;
    }

    private flipIndexedGeometryWinding(geometry: THREE.BufferGeometry): void {
        const index = geometry.getIndex();
        if (!index) {
            return;
        }

        const arr = index.array as ArrayLike<number> & { [n: number]: number };

        for (let i = 0; i < arr.length; i += 3) {
            const tmp = arr[i + 1];
            arr[i + 1] = arr[i + 2];
            arr[i + 2] = tmp;
        }

        index.needsUpdate = true;
    }

    private resolveSectionYAxis(
        tangent: THREE.Vector3,
        preferredNormal?: Vector3DDto
    ): THREE.Vector3 {
        let yAxis = preferredNormal
            ? this.toVector3(preferredNormal)
            : (Math.abs(tangent.z) < 0.9
                ? new THREE.Vector3(0, 0, 1)
                : new THREE.Vector3(0, 1, 0));

        // project to plane perpendicular to tangent
        yAxis = yAxis.clone().addScaledVector(tangent, -yAxis.dot(tangent));

        if (yAxis.lengthSq() < 1e-12) {
            const fallback = Math.abs(tangent.y) < 0.9
                ? new THREE.Vector3(0, 1, 0)
                : new THREE.Vector3(1, 0, 0);

            yAxis = fallback.addScaledVector(tangent, -fallback.dot(tangent));
        }

        return yAxis.normalize();
    }

   

    private buildPath3D(def: Path3DDefinition): THREE.Line | null {
        if (!def.visible && def.visible !== undefined) return null;
        if (!def.points || def.points.length < 2) return null;

        const points = def.points.map(p => new THREE.Vector3(p[0], p[1], p[2]));
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        const color = def.color ?? this.options.edgeColor;

        let line: THREE.Line;

        if (def.style === 'dashed') {
            const material = new THREE.LineDashedMaterial({
                color,
                dashSize: 10,
                gapSize: 5
            });
            line = new THREE.Line(geometry, material);
            line.computeLineDistances();
        } else {
            const material = new THREE.LineBasicMaterial({ color });
            line = new THREE.Line(geometry, material);
        }

        if (def.handleId) {
            line.userData.handleId = def.handleId;
            this.objectsByHandleId.set(def.handleId, line);
            this.visibilityObjectsByHandleId.set(def.handleId, line);
        }

        return line;
    }

    private createSolidMaterial(color?: number, opacity?: number): THREE.MeshStandardMaterial {
        return new THREE.MeshStandardMaterial({
            color: color ?? this.options.solidColor,
            transparent: opacity !== undefined && opacity < 1,
            opacity: opacity ?? 1,
            metalness: 0.0,
            roughness: 0.9,
            side: THREE.DoubleSide,
        });
    }


    private createCutDebugMaterial(): THREE.MeshStandardMaterial {
        return new THREE.MeshStandardMaterial({
            color: this.options.cutDebugColor,
            transparent: true,
            opacity: this.options.cutDebugOpacity,
            metalness: 0,
            roughness: 0.8,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
    }

    private createEdges(geometry: THREE.BufferGeometry): THREE.LineSegments {
        const edgeSource = geometry.index
            ? geometry.clone().toNonIndexed()
            : geometry.clone();

        if (edgeSource.getAttribute('normal')) edgeSource.deleteAttribute('normal');
        if (edgeSource.getAttribute('uv')) edgeSource.deleteAttribute('uv');
        edgeSource.clearGroups();

        edgeSource.computeBoundingBox();
        const size = edgeSource.boundingBox!.getSize(new THREE.Vector3());
        const diag = Math.max(size.length(), 1);

        const weldEps = Math.max(diag * 1e-6, 1e-5);
        const planeEps = weldEps * 2;
        const creaseDot = Math.cos(THREE.MathUtils.degToRad(this.options.edgeThresholdAngle));

        const welded = BufferGeometryUtils.mergeVertices(edgeSource, weldEps);
        welded.computeVertexNormals();
        welded.computeBoundingBox();
        welded.computeBoundingSphere();

        const index = welded.getIndex();
        const pos = welded.getAttribute('position') as THREE.BufferAttribute;

        if (!index || pos.count < 3) {
            const empty = new THREE.BufferGeometry();
            return new THREE.LineSegments(
                empty,
                new THREE.LineBasicMaterial({ color: this.options.edgeColor })
            );
        }

        type FaceInfo = {
            a: number;
            b: number;
            c: number;
            normal: THREE.Vector3;
            plane: THREE.Plane;
            tri: THREE.Triangle;
        };

        const faces: FaceInfo[] = [];
        const ia = index.array as ArrayLike<number>;

        const va = new THREE.Vector3();
        const vb = new THREE.Vector3();
        const vc = new THREE.Vector3();

        for (let i = 0; i < ia.length; i += 3) {
            const a = ia[i + 0];
            const b = ia[i + 1];
            const c = ia[i + 2];

            va.fromBufferAttribute(pos, a);
            vb.fromBufferAttribute(pos, b);
            vc.fromBufferAttribute(pos, c);

            const tri = new THREE.Triangle(va.clone(), vb.clone(), vc.clone());
            const normal = new THREE.Vector3();
            tri.getNormal(normal);

            // degenerate triangle overslaan
            if (normal.lengthSq() < 1e-16) continue;

            const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, tri.a);
            faces.push({ a, b, c, normal, plane, tri });
        }

        type EdgeEntry = {
            a: number;
            b: number;
            faces: number[];
        };

        const edgeMap = new Map<string, EdgeEntry>();

        const addEdge = (i0: number, i1: number, faceIndex: number) => {
            const a = Math.min(i0, i1);
            const b = Math.max(i0, i1);
            const key = `${a}_${b}`;

            let entry = edgeMap.get(key);
            if (!entry) {
                entry = { a, b, faces: [] };
                edgeMap.set(key, entry);
            }

            entry.faces.push(faceIndex);
        };

        for (let f = 0; f < faces.length; f++) {
            const face = faces[f];
            addEdge(face.a, face.b, f);
            addEdge(face.b, face.c, f);
            addEdge(face.c, face.a, f);
        }

        const p0 = new THREE.Vector3();
        const p1 = new THREE.Vector3();
        const mid = new THREE.Vector3();
        const closest = new THREE.Vector3();

        const linePositions: number[] = [];

        for (const entry of edgeMap.values()) {
            pos.getX(entry.a); // no-op to keep TS happy in some setups
            p0.fromBufferAttribute(pos, entry.a);
            p1.fromBufferAttribute(pos, entry.b);
            mid.copy(p0).add(p1).multiplyScalar(0.5);

            const adjacent = entry.faces.map(fi => faces[fi]);

            let keep = false;

            if (adjacent.length === 1) {
                // boundary edge -> voorlopig kandidaat
                keep = true;

                // maar: als het middelpunt al in een andere coplanaire triangle ligt,
                // dan is dit meestal een vlakliggende interne seam / T-junction edge
                const ref = adjacent[0];

                for (let f = 0; f < faces.length; f++) {
                    if (entry.faces.includes(f)) continue;

                    const other = faces[f];
                    const samePlane =
                        Math.abs(ref.normal.dot(other.normal) - 1) < 1e-4 &&
                        Math.abs(ref.plane.distanceToPoint(mid)) < planeEps &&
                        Math.abs(other.plane.distanceToPoint(mid)) < planeEps;

                    if (!samePlane) continue;

                    other.tri.closestPointToPoint(mid, closest);
                    if (closest.distanceToSquared(mid) < planeEps * planeEps) {
                        keep = false;
                        break;
                    }
                }
            } else {
                // bij 2 faces: alleen houden als het echt een crease is
                let isCrease = false;

                for (let i = 0; i < adjacent.length && !isCrease; i++) {
                    for (let j = i + 1; j < adjacent.length; j++) {
                        if (adjacent[i].normal.dot(adjacent[j].normal) < creaseDot) {
                            isCrease = true;
                            break;
                        }
                    }
                }

                keep = isCrease;
            }

            if (keep) {
                linePositions.push(
                    p0.x, p0.y, p0.z,
                    p1.x, p1.y, p1.z
                );
            }
        }

        const edgesGeometry = new THREE.BufferGeometry();
        edgesGeometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(linePositions, 3)
        );
        edgesGeometry.computeBoundingBox();
        edgesGeometry.computeBoundingSphere();

        const edgesMaterial = new THREE.LineBasicMaterial({
            color: this.options.edgeColor
        });

        return new THREE.LineSegments(edgesGeometry, edgesMaterial);
    }

    public setModelTransparency(enabled: boolean, opacity = this.modelTransparencyOpacity): void {
        this.modelTransparencyEnabled = enabled;
        this.modelTransparencyOpacity = THREE.MathUtils.clamp(opacity, 0, 1);
        this.applyModelTransparency();
    }

    private applyModelTransparency(): void {
        this.modelRoot.traverse(obj => {
            if (!(obj instanceof THREE.Mesh)) {
                return;
            }

            this.applyTransparencyToMaterialValue(obj.material);
        });

        // belangrijk: als een mesh geselecteerd is, zit het originele materiaal
        // tijdelijk in deze cache en niet op de mesh zelf
        for (const material of this.originalMaterials.values()) {
            this.applyTransparencyToMaterialValue(material);
        }
    }

    private applyTransparencyToMaterialValue(material: THREE.Material | THREE.Material[]): void {
        if (Array.isArray(material)) {
            material.forEach(m => this.applyTransparencyToMaterial(m));
            return;
        }

        this.applyTransparencyToMaterial(material);
    }

    private applyTransparencyToMaterial(material: THREE.Material): void {
        if (!this.materialVisualState.has(material)) {
            this.materialVisualState.set(material, {
                transparent: material.transparent,
                opacity: material.opacity,
                depthWrite: material.depthWrite,
            });
        }

        const original = this.materialVisualState.get(material)!;

        if (this.modelTransparencyEnabled) {
            material.transparent = true;
            material.opacity = this.modelTransparencyOpacity;
            material.depthWrite = false;
        } else {
            material.transparent = original.transparent;
            material.opacity = original.opacity;
            material.depthWrite = original.depthWrite;
        }

        material.needsUpdate = true;
    }

    private setOrbitTargetFromPointerEvent(event: PointerEvent): void {
        // niet reageren als je op de view cube zelf dubbel-rechts klikt
        if (this.isInsideGizmo(event.clientX, event.clientY)) {
            return;
        }

        const rect = this.renderer.domElement.getBoundingClientRect();

        this.pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.pointerNdc, this.activeCamera);

        const intersects = this.raycaster.intersectObjects(this.modelRoot.children, true);
        const first = intersects.find(i => i.object instanceof THREE.Mesh);

        if (!first) {
            return;
        }

        // lopende camera-animatie stoppen
        this.cameraAnim = undefined;

        this.controls.target.copy(first.point);

        // optioneel handig als je later target-radius limits gebruikt
        if ('cursor' in this.controls && this.controls.cursor instanceof THREE.Vector3) {
            this.controls.cursor.copy(first.point);
        }

        this.activeCamera.lookAt(this.controls.target);
        this.controls.update();
        this.render();
    }

    private isInsideGizmo(clientX: number, clientY: number): boolean {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const size = 100;
        const margin = 12;

        const left = rect.right - size - margin;
        const top = rect.bottom - size - margin;

        return (
            clientX >= left &&
            clientX <= left + size &&
            clientY >= top &&
            clientY <= top + size
        );
    }

    private updateHoverPick(clientX: number, clientY: number): void {
        if (this.isInsideGizmo(clientX, clientY)) {
            this.hoverHitValid = false;
            this.hoverMarker.visible = false;
            return;
        }

        const rect = this.renderer.domElement.getBoundingClientRect();
        this.pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this.pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.pointerNdc, this.activeCamera);

        const hits = this.raycaster.intersectObjects(this.modelRoot.children, true);
        const first = hits.find(i => i.object instanceof THREE.Mesh);

        if (!first) {
            this.hoverHitValid = false;
            this.hoverMarker.visible = false;
            return;
        }

        this.hoverHitValid = true;
        this.hoverPoint.copy(first.point);

        this.hoverMarker.position.copy(first.point);
        this.hoverMarker.visible = true;

        const now = performance.now();
        if (now - this.lastHoverNotifyAt > 50) {
            this.lastHoverNotifyAt = now;
            void this.notifyHoverPoint(this.hoverPoint);
        }
    }

    private async notifyHoverPoint(point: THREE.Vector3): Promise<void> {
        if (!this.blazorRef) return;

        await this.blazorRef.invokeMethodAsync('OnHoverPointChanged', {
            x: point.x,
            y: point.y,
            z: point.z
        });
    }

    private async notifyMeasurement(distance: number, a: THREE.Vector3, b: THREE.Vector3): Promise<void> {
        if (!this.blazorRef) return;

        await this.blazorRef.invokeMethodAsync('OnMeasurementChanged', {
            distance,
            from: { x: a.x, y: a.y, z: a.z },
            to: { x: b.x, y: b.y, z: b.z }
        });
    }

    private onCanvasClick = (event: MouseEvent): void => {

        if (this.tryHandleGizmoClick(event)) {
            return;
        }

        if (!this.options.enableSelection) return;

        const rect = this.renderer.domElement.getBoundingClientRect();
        this.pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.pointerNdc, this.activeCamera);
        const intersects = this.raycaster.intersectObjects(this.modelRoot.children, true);
        const first = intersects.find(x => Boolean(x.object.userData.handleId || x.object.parent?.userData.handleId));

        if (!first) {
            this.clearSelection();
            void this.notifySelection(undefined);
            return;
        }

        if (first?.point) {
            this.registerMeasurementPoint(first.point);
        }

        const handleId = (first.object.userData.handleId ?? first.object.parent?.userData.handleId) as string | undefined;
        this.selectByHandleId(handleId, true);
    };

    private tryHandleGizmoClick(event: MouseEvent): boolean {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const size = 100;
        const margin = 12;

        const left = rect.right - size - margin;
        const top = rect.bottom - size - margin;

        const inside =
            event.clientX >= left &&
            event.clientX <= left + size &&
            event.clientY >= top &&
            event.clientY <= top + size;

        if (!inside) return false;

        const localX = event.clientX - left;
        const localY = event.clientY - top;

        this.gizmoPointer.x = (localX / size) * 2 - 1;
        this.gizmoPointer.y = -((localY / size) * 2 - 1);

        this.gizmoRaycaster.setFromCamera(this.gizmoPointer, this.gizmoCamera);
        const hits = this.gizmoRaycaster.intersectObjects(this.gizmoRoot.children, true);
        const hit = hits.find(h => ['right', 'left', 'top', 'bottom', 'front', 'back'].includes(h.object.name));

        if (!hit) return true;

        this.snapCameraToNamedView(hit.object.name as 'right' | 'left' | 'top' | 'bottom' | 'front' | 'back');
        return true;
    }

    private async notifySelection(handleId: string | undefined): Promise<void> {
        if (!this.blazorRef) return;
        await this.blazorRef.invokeMethodAsync('OnExtrusionSelected', handleId ?? null);
    }

    private disposeObject(object: THREE.Object3D, includeSelf = true): void {
        object.traverse(child => {
            const anyChild = child as any;

            if (anyChild.geometry) {
                anyChild.geometry.dispose();
            }

            const material = anyChild.material;
            if (Array.isArray(material)) {
                material.forEach(m => m.dispose());
            } else if (material) {
                material.dispose();
            }
        });

        if (includeSelf && object.parent) {
            object.parent.remove(object);
        }
    }

    private cameraAnim?: {
        startTime: number;
        durationMs: number;
        fromPos: THREE.Vector3;
        toPos: THREE.Vector3;
        fromUp: THREE.Vector3;
        toUp: THREE.Vector3;
        target: THREE.Vector3;
    };

    private snapCameraToNamedView(
        name: 'right' | 'left' | 'top' | 'bottom' | 'front' | 'back'
    ): void {
        const box = this.lastModelBox.isEmpty()
            ? new THREE.Box3().setFromObject(this.modelRoot)
            : this.lastModelBox.clone();

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const views = {
            right: { dir: new THREE.Vector3(1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
            left: { dir: new THREE.Vector3(-1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
            front: { dir: new THREE.Vector3(0, 0, 1), up: new THREE.Vector3(0, 1, 0) },
            back: { dir: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0) },
            top: { dir: new THREE.Vector3(0, 1, 0), up: new THREE.Vector3(0, 0, -1) },
            bottom: { dir: new THREE.Vector3(0, -1, 0), up: new THREE.Vector3(0, 0, 1) },
        } as const;

        const view = views[name];

        // afstand behouden t.o.v. target
        const currentTarget = this.controls.target.clone();
        const currentDistance = this.activeCamera.position.distanceTo(currentTarget);

        // fallback als target niet mooi in het modelcentrum ligt
        const target = center;

        // iets robuuster voor hele platte modellen
        const distance = Math.max(currentDistance, Math.max(size.x, size.y, size.z, 1) * 1.5);

        const toPos = target.clone().add(view.dir.clone().multiplyScalar(distance));

        this.cameraAnim = {
            startTime: performance.now(),
            durationMs: 350,
            fromPos: this.activeCamera.position.clone(),
            toPos,
            fromUp: this.activeCamera.up.clone(),
            toUp: view.up.clone(),
            target
        };
    }

    private resolveMaterialColor(material: THREE.Material | THREE.Material[]): number {
        const first = Array.isArray(material) ? material[0] : material;
        const withColor = first as THREE.Material & { color?: THREE.Color };

        return withColor.color?.getHex?.() ?? this.options.solidColor;
    }

    private extractTrianglePolygonsFromMesh(mesh: THREE.Mesh): Polygon3DDto[] {
        const geometry = mesh.geometry;

        if (!(geometry instanceof THREE.BufferGeometry)) {
            return [];
        }

        const position = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
        if (!position) {
            return [];
        }

        const polygons: Polygon3DDto[] = [];
        const temp = new THREE.Vector3();

        const readPoint = (vertexIndex: number): Point3DDto => {
            temp.fromBufferAttribute(position, vertexIndex).applyMatrix4(mesh.matrixWorld);

            return {
                x: temp.x,
                y: temp.y,
                z: temp.z
            };
        };

        const index = geometry.getIndex();

        if (index) {
            const arr = index.array as ArrayLike<number>;

            for (let i = 0; i + 2 < arr.length; i += 3) {
                polygons.push({
                    points: [
                        readPoint(Number(arr[i])),
                        readPoint(Number(arr[i + 1])),
                        readPoint(Number(arr[i + 2]))
                    ]
                });
            }
        } else {
            for (let i = 0; i + 2 < position.count; i += 3) {
                polygons.push({
                    points: [
                        readPoint(i),
                        readPoint(i + 1),
                        readPoint(i + 2)
                    ]
                });
            }
        }

        return polygons;
    }

    public getRenderedExtrusionPolygons(): ExtrusionPolygonSetDto[] {
        const result: ExtrusionPolygonSetDto[] = [];

        this.modelRoot.updateMatrixWorld(true);

        this.modelRoot.traverseVisible(obj => {
            if (!(obj instanceof THREE.Mesh)) {
                return;
            }

            const handleIds = Array.isArray(obj.userData.sourceHandleIds)
                ? obj.userData.sourceHandleIds.filter((x: unknown): x is string => typeof x === 'string')
                : (typeof obj.userData.handleId === 'string' ? [obj.userData.handleId] : []);

            if (handleIds.length === 0) {
                return;
            }

            const polygons = this.extractTrianglePolygonsFromMesh(obj);
            if (polygons.length === 0) {
                return;
            }

            const colorByHandleId = (obj.userData.exportColorByHandleId ?? {}) as Record<string, number>;
            const fallbackColor = this.resolveMaterialColor(obj.material);

            for (const handleId of handleIds) {
                result.push({
                    handleId,
                    color: typeof colorByHandleId[handleId] === 'number'
                        ? colorByHandleId[handleId]
                        : fallbackColor,
                    polygons
                });
            }
        });

        return result;
    }

    public async sendRenderedExtrusionPolygonsToBlazor(): Promise<void> {
        if (!this.blazorRef) {
            return;
        }

        const payload = this.getRenderedExtrusionPolygons();
        await this.blazorRef.invokeMethodAsync('OnRenderedExtrusionPolygonsReady', payload);
    }

    private toArgbColor(color: number, opacity?: number): number {
        const a = Math.round(THREE.MathUtils.clamp(opacity ?? 1, 0, 1) * 255);
        const rgb = color & 0x00ffffff;

        // force signed 32-bit int voor Blazor int
        return ((a << 24) | rgb) | 0;
    }
}

declare global {
    interface Window {
        cadViewer?: {
            create: (host: HTMLElement, options?: ViewerOptions) => string;
            setBlazorRef: (viewerId: string, ref: BlazorRef | undefined) => void;
            loadModel: (viewerId: string, request: LoadModelRequest) => Promise<void>;
            loadMockModel: (viewerId: string) => Promise<void>;
            setModel: (viewerId: string, dto: IntermediateModelDto) => void;
            setProjectionMode: (viewerId: string, mode: 'perspective' | 'orthographic') => void;
            toggleProjectionMode: (viewerId: string) => void;
            setModelTransparency: (viewerId: string, enabled: boolean, opacity?: number) => void;
            setHandlesVisibility: (viewerId: string, handleIds: string[], visible: boolean) => void;
            zoomAll: (viewerId: string, paddingFactor?: number) => void;
            clearSelection: (viewerId: string) => void;
            selectByHandleId: (viewerId: string, handleId?: string, notifyBlazor?: boolean) => void;
            getRenderedExtrusionPolygons: (viewerId: string) => ExtrusionPolygonSetDto[];
            sendRenderedExtrusionPolygonsToBlazor: (viewerId: string) => Promise<void>;
            dispose: (viewerId: string) => void;
        };
    }
}

const viewerRegistry = new Map<string, ThreeCadViewer>();

function createViewerId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }

    return `viewer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
async function mockFetchIntermediateModel(): Promise<IntermediateModelDto> {
    await new Promise(resolve => window.setTimeout(resolve, 120));

    const path2d: Vec2[] = [
        [-22, 0],
        [-18, 4],
        [-18, 11],
        [-26, 11],
        [-26, 16],
        [-3.0397742875339424, 16],
        [-2.0414654048937755, 16.941867609358106],
        [0, 52],
        [-4.000000000000001, 56],
        [-75.21467953445853, 56],
        [-78.11245701332572, 53.77645713530756],
        [-81, 43],
        [-64, 43],
        [-64, 3],
        [-61, 0],
        [-22, 0]
    ];

    return {
        units: 'mm',
        extrusions: [
            {
                handleId: 'SEG-Top-0',
                bodyId: 'BODY-SEG-Top-0',
                kind: 'solid',
                path2d,
                length: 927.5000024815693,
                transform: {
                    elements: [
                        3.4228541778702493E-08, 0.99999996577145822, 0, 52.999998185887307,
                        0, 0, 0.99999996577145822, 2948.0000017798843,
                        1, 0, 0, 35.999998767772496,
                        0, 0, 0, 1
                    ]
                },
                color: 0x8fa3bf,
            },
            {
                handleId: 'SEG-Top-1',
                bodyId: 'BODY-SEG-Top-0',
                kind: 'cut',
                path2d,
                length: 927.5000024815693,
                transform: {
                    elements: [
                        3.4228541778702493E-08, 0.99999996577145822, 0, 82.999998185887307,
                        0, 0, 0.99999996577145822, 2958.0000017798843,
                        1, 0, 0, 45.999998767772496,
                        0, 0, 0, 1
                    ]
                }

            }
        ],
        paths3d: []
    };
}

window.cadViewer = {
    create: (host: HTMLElement, options?: ViewerOptions): string => {
        const viewerId = createViewerId();
        const viewer = new ThreeCadViewer(host, options);
        viewerRegistry.set(viewerId, viewer);
        return viewerId;
    },

    setBlazorRef: (viewerId: string, ref: BlazorRef | undefined): void => {
        const viewer = viewerRegistry.get(viewerId);
        viewer?.setBlazorRef(ref);
    },

    loadModel: async (viewerId: string, request: LoadModelRequest): Promise<void> => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        await viewer.loadModel(request);
    },

    loadMockModel: async (viewerId: string): Promise<void> => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        await viewer.loadMockModel();
    },

    setModel: async (viewerId: string, dto: IntermediateModelDto): Promise<void> => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        await viewer.setModel(dto);
    },

    setProjectionMode: (viewerId: string, mode: 'perspective' | 'orthographic'): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.setProjectionMode(mode);
    },

    toggleProjectionMode: (viewerId: string): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.toggleProjectionMode();
    },

    setModelTransparency: (viewerId: string, enabled: boolean, opacity?: number): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.setModelTransparency(enabled, opacity);
    },

    setHandlesVisibility: (viewerId: string, handleIds: string[], visible: boolean): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.setHandlesVisibility(handleIds, visible);
    },

    zoomAll: (viewerId: string, paddingFactor?: number): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.zoomAll(paddingFactor);
    },

    clearSelection: (viewerId: string): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.clearSelection();
    },

    selectByHandleId: (viewerId: string, handleId?: string, notifyBlazor?: boolean): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        viewer.selectByHandleId(handleId, notifyBlazor);
    },

    getRenderedExtrusionPolygons: (viewerId: string): ExtrusionPolygonSetDto[] => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        return viewer.getRenderedExtrusionPolygons();
    },

    sendRenderedExtrusionPolygonsToBlazor: async (viewerId: string): Promise<void> => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            throw new Error(`Viewer '${viewerId}' not found.`);
        }

        await viewer.sendRenderedExtrusionPolygonsToBlazor();
    },

    dispose: (viewerId: string): void => {
        const viewer = viewerRegistry.get(viewerId);
        if (!viewer) {
            return;
        }

        viewer.dispose();
        viewerRegistry.delete(viewerId);
    },
};
